import React, { memo } from 'react';
import { BarChart2, GitCompare, ArrowLeftRight, Database, Clock } from 'lucide-react';
import type { StepCounters } from '../../../core/utils/statePlayer';

export interface StatsPanelProps {
  counters: StepCounters;
  totalSteps: number;
  currentStepIndex: number;
  spaceComplexity?: string;
  timeComplexity?: string;
}

interface TickerProps {
  label: string;
  value: number;
  icon: React.ReactNode;
  iconBg: string;
  valueColor: string;
}

const Ticker: React.FC<TickerProps> = ({ label, value, icon, iconBg, valueColor }) => (
  <div className="stat-chip">
    <div className="icon-box flex-shrink-0" style={{ background: iconBg }}>
      {icon}
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[9px] font-semibold tracking-[0.1em] uppercase leading-none mb-0.5" style={{ color: '#52525b' }}>
        {label}
      </p>
      <p className="text-[15px] font-bold font-mono tabular-nums leading-none" style={{ color: valueColor }}>
        {value.toLocaleString()}
      </p>
    </div>
  </div>
);

export const StatsPanel: React.FC<StatsPanelProps> = memo(({
  counters,
  totalSteps,
  currentStepIndex,
  spaceComplexity = 'O(1)',
  timeComplexity  = 'O(n²)',
}) => {
  const progress = totalSteps > 0
    ? Math.round(((currentStepIndex + 1) / totalSteps) * 100)
    : 0;

  return (
    <div className="flex flex-col gap-2.5">

      {/* Header + progress */}
      <div className="flex items-center gap-2">
        <BarChart2 className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#3b82f6' }} />
        <span className="text-[10px] font-semibold tracking-[0.1em] uppercase" style={{ color: '#52525b' }}>
          Live Stats
        </span>
        <span className="ml-auto text-[10px] font-mono tabular-nums" style={{ color: '#3f3f46' }}>
          {progress}%
        </span>
      </div>

      {/* Progress bar */}
      <div className="h-px rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.07)' }}>
        <div
          className="h-full rounded-full transition-all duration-300"
          style={{
            width: `${progress}%`,
            background: 'linear-gradient(90deg, #3b82f6 0%, #6366f1 100%)',
          }}
        />
      </div>

      {/* Counters 2×2 */}
      <div className="grid grid-cols-2 gap-1.5">
        <Ticker
          label="Comparisons"
          value={counters.comparisons}
          icon={<GitCompare className="w-3.5 h-3.5" style={{ color: '#d97706' }} />}
          iconBg="rgba(245,158,11,0.13)"
          valueColor="#fbbf24"
        />
        <Ticker
          label="Swaps"
          value={counters.swaps}
          icon={<ArrowLeftRight className="w-3.5 h-3.5" style={{ color: '#ef4444' }} />}
          iconBg="rgba(239,68,68,0.13)"
          valueColor="#f87171"
        />
        <Ticker
          label="Updates"
          value={counters.updates}
          icon={<Clock className="w-3.5 h-3.5" style={{ color: '#60a5fa' }} />}
          iconBg="rgba(59,130,246,0.13)"
          valueColor="#93c5fd"
        />
        <Ticker
          label="Accesses"
          value={counters.accesses}
          icon={<Database className="w-3.5 h-3.5" style={{ color: '#a78bfa' }} />}
          iconBg="rgba(139,92,246,0.13)"
          valueColor="#c4b5fd"
        />
      </div>

      {/* Complexity row */}
      <div className="flex gap-1.5">
        <div
          className="flex-1 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg"
          style={{
            background: 'rgba(16,185,129,0.06)',
            border: '1px solid rgba(16,185,129,0.14)',
          }}
        >
          <Clock className="w-3 h-3 flex-shrink-0" style={{ color: '#10b981' }} />
          <span className="text-[10px]" style={{ color: '#52525b' }}>Time</span>
          <span className="text-[10px] font-mono font-bold ml-auto" style={{ color: '#34d399' }}>
            {timeComplexity}
          </span>
        </div>
        <div
          className="flex-1 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg"
          style={{
            background: 'rgba(139,92,246,0.06)',
            border: '1px solid rgba(139,92,246,0.14)',
          }}
        >
          <Database className="w-3 h-3 flex-shrink-0" style={{ color: '#8b5cf6' }} />
          <span className="text-[10px]" style={{ color: '#52525b' }}>Space</span>
          <span className="text-[10px] font-mono font-bold ml-auto" style={{ color: '#a78bfa' }}>
            {spaceComplexity}
          </span>
        </div>
      </div>
    </div>
  );
});
